import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Grow,
  Zoom,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
  TableSortLabel,
  InputAdornment
} from '@mui/material';

import {
  Inventory,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Schedule,
  Block,
  Refresh,
  Download,
  Assessment,
  Storefront,
  Category,
  LocalShipping,
  AssignmentTurnedIn,
  Verified,
  ThumbUp,
  HourglassEmpty,
  Receipt,
  Search,
  Clear,
  CalendarToday,
  Person,
  Business,
  Print,
  FileDownload,
  Visibility,
  Analytics,
  ShowChart,
  PieChart as PieChartIcon,
  BarChart,
  LineChart,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  AddCircle,
  RemoveCircle
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import Sidebar from '../../components/sidebars/sidebar_gm';
import TopNav from '../../components/top_bars/TopNav_inventory';
import axios from 'axios';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  }
}));

const MetricCard = styled(Card)(({ theme, color }) => ({
  background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
  borderRadius: '16px',
  borderLeft: `4px solid ${color}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  }
}));

const ExpiryAlertCard = styled(Card)(({ theme, severity }) => ({
  borderRadius: '12px',
  marginBottom: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderLeft: `4px solid ${
    severity === 'expired' ? theme.palette.error.main :
    severity === 'critical' ? theme.palette.error.main :
    severity === 'warning' ? theme.palette.warning.main :
    theme.palette.info.main
  }`,
  '&:hover': {
    transform: 'translateX(4px)',
    boxShadow: theme.shadows[4]
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  overflowX: 'auto',
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  }
}));

const GMInventoryDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [userRole, setUserRole] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Inventory table states
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryRowsPerPage, setInventoryRowsPerPage] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('item_name');
  const [order, setOrder] = useState('asc');
  
  // Data states
  const [inventoryStats, setInventoryStats] = useState({
    total_items: 0,
    total_value: 0,
    low_stock_items: 0,
    out_of_stock: 0,
    expiring_soon: 0,
    expired_items: 0,
    monthly_movement: [],
    category_distribution: [],
    top_moving_items: [],
    slow_moving_items: []
  });
  
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [expiredProducts, setExpiredProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [recentRequisitions, setRecentRequisitions] = useState([]);
  const [departmentConsumption, setDepartmentConsumption] = useState([]);
  
  // Alert dialog
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // Fetch inventory items for table
  const fetchInventoryItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (inventorySearch) params.append('search', inventorySearch);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      params.append('limit', inventoryRowsPerPage);
      params.append('offset', inventoryPage * inventoryRowsPerPage);
      if (orderBy) params.append('order_by', orderBy);
      if (order) params.append('order', order);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_inventory_items.php?${params}`);
      
      if (response.data.success) {
        setInventoryItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  }, [inventorySearch, categoryFilter, inventoryPage, inventoryRowsPerPage, orderBy, order]);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory/gm_dashboard_data.php`);
      
      if (response.data.success) {
        setInventoryStats(response.data.stats);
        setExpiringProducts(response.data.expiring_soon || []);
        setExpiredProducts(response.data.expired || []);
        setLowStockProducts(response.data.low_stock || []);
        setRecentRequisitions(response.data.recent_requisitions || []);
        setDepartmentConsumption(response.data.department_consumption || []);
        
        // Show alert popup if there are expiring products
        if (response.data.expiring_soon?.length > 0 || response.data.expired?.length > 0) {
          setAlertDialogOpen(true);
        }
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to fetch dashboard data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchInventoryItems();
  }, [fetchDashboardData, fetchInventoryItems]);

  useEffect(() => {
    fetchInventoryItems();
  }, [inventorySearch, categoryFilter, inventoryPage, inventoryRowsPerPage, orderBy, order, fetchInventoryItems]);

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const role = localStorage.getItem('role');
    
    setLoggedInUser(userName || userEmail || 'GM');
    setUserRole(role || 'staff');
  }, []);

  const isAuthorizedGM = () => {
    return userRole === 'gm' || userRole === 'admin' || userRole === 'general_manager';
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Export all data to Excel
  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const workbookData = {
        'Inventory_Stock_Summary': inventoryItems.map(item => ({
          'Item Name': item.item_name,
          'Category': item.category,
          'Quantity Supplied': item.quantity_received,
          'Quantity Issued': item.quantity_issued,
          'Quantity Left': item.quantity_available,
          'Unit': item.unit_of_measurement,
          'Low Stock Threshold': item.lowstock_threshold,
          'Storage Location': item.storage_location || 'N/A',
          'Last Updated': item.updated_at || item.created_at,
          'Status': item.quantity_available <= item.lowstock_threshold ? 'Low Stock' : 'In Stock'
        })),
        'Inventory_Summary': [{
          'Total Items': inventoryStats.total_items,
          'Total Value': inventoryStats.total_value,
          'Low Stock Items': inventoryStats.low_stock_items,
          'Out of Stock': inventoryStats.out_of_stock,
          'Expiring Soon': inventoryStats.expiring_soon,
          'Expired Items': inventoryStats.expired_items
        }],
        'Expiring_Products': expiringProducts.map(p => ({
          'Item Name': p.item_name,
          'Expiry Date': p.expiry_date,
          'Days Until Expiry': p.days_until_expiry,
          'Quantity': p.quantity_received,
          'Unit': p.unit_of_measurement,
          'Storage Location': p.storage_location || 'N/A'
        })),
        'Expired_Products': expiredProducts.map(p => ({
          'Item Name': p.item_name,
          'Expiry Date': p.expiry_date,
          'Days Overdue': p.days_overdue,
          'Quantity': p.quantity_received,
          'Unit': p.unit_of_measurement
        })),
        'Low_Stock_Products': lowStockProducts.map(p => ({
          'Item Name': p.item_name,
          'Current Stock': p.current_stock,
          'Threshold': p.threshold,
          'Unit': p.unit_of_measurement,
          'Status': p.status
        })),
        'Recent_Requisitions': recentRequisitions.map(r => ({
          'ID': r.id,
          'Department': r.department,
          'Item': r.item_name,
          'Quantity': r.quantity_requested,
          'Requested By': r.requested_by,
          'Date': r.request_date,
          'Status': r.status
        }))
      };
      
      const wb = XLSX.utils.book_new();
      for (const [sheetName, data] of Object.entries(workbookData)) {
        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
      }
      
      XLSX.writeFile(wb, `GM_Inventory_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setSnackbar({
        open: true,
        message: 'Export successful!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export data',
        severity: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  // Chart configurations
  const categoryChartData = {
    labels: inventoryStats.category_distribution?.map(c => c.category) || [],
    datasets: [
      {
        label: 'Stock Value (₦)',
        data: inventoryStats.category_distribution?.map(c => c.total_value) || [],
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const movementChartData = {
    labels: inventoryStats.monthly_movement?.map(m => m.month) || [],
    datasets: [
      {
        label: 'Items Received',
        data: inventoryStats.monthly_movement?.map(m => m.received) || [],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
      {
        label: 'Items Issued',
        data: inventoryStats.monthly_movement?.map(m => m.issued) || [],
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      },
    ],
  };

  const departmentChartData = {
    labels: departmentConsumption.map(d => d.department),
    datasets: [
      {
        label: 'Items Consumed',
        data: departmentConsumption.map(d => d.total_consumed),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { size: isMobile ? 10 : 12 } }
      }
    }
  };

  // Helper function to get status color
  const getExpiryStatus = (daysUntil) => {
    if (daysUntil <= 0) return { color: 'error', label: 'Expired', severity: 'expired' };
    if (daysUntil <= 7) return { color: 'error', label: 'Critical', severity: 'critical' };
    if (daysUntil <= 14) return { color: 'warning', label: 'Warning', severity: 'warning' };
    return { color: 'info', label: 'Soon', severity: 'info' };
  };

  // Get stock status chip
  const getStockStatusChip = (available, threshold) => {
    if (available <= 0) {
      return <Chip size="small" label="Out of Stock" color="error" icon={<Block />} />;
    }
    if (available <= threshold) {
      return <Chip size="small" label="Low Stock" color="warning" icon={<Warning />} />;
    }
    return <Chip size="small" label="In Stock" color="success" icon={<CheckCircle />} />;
  };

  // Render Inventory Stock Table (NEW - First Tab)
  const renderInventoryStockTable = () => (
    <StyledCard>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <Typography variant="h6" fontWeight={600}>
            <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
            Current Inventory Stock
          </Typography>
          
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder="Search items..."
              value={inventorySearch}
              onChange={(e) => setInventorySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: inventorySearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setInventorySearch('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ width: 200 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="Furniture">Furniture</MenuItem>
                <MenuItem value="Stationery">Stationery</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
        
        <StyledTableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'item_name'}
                    direction={orderBy === 'item_name' ? order : 'asc'}
                    onClick={() => handleSort('item_name')}
                  >
                    Item Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'quantity_received'}
                    direction={orderBy === 'quantity_received' ? order : 'asc'}
                    onClick={() => handleSort('quantity_received')}
                  >
                    Qty Supplied
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'quantity_issued'}
                    direction={orderBy === 'quantity_issued' ? order : 'asc'}
                    onClick={() => handleSort('quantity_issued')}
                  >
                    Qty Issued
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'quantity_available'}
                    direction={orderBy === 'quantity_available' ? order : 'asc'}
                    onClick={() => handleSort('quantity_available')}
                  >
                    Qty Left
                  </TableSortLabel>
                </TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Threshold</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Storage Location</TableCell>
                <TableCell>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventoryItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No inventory items found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inventoryItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.item_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.category || 'Uncategorized'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {Number(item.quantity_received).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {Number(item.quantity_issued).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        color={item.quantity_available <= item.lowstock_threshold ? 'error.main' : 'success.main'}
                      >
                        {Number(item.quantity_available).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.unit_of_measurement || 'Units'}</TableCell>
                    <TableCell>{item.lowstock_threshold || 0}</TableCell>
                    <TableCell>
                      {getStockStatusChip(item.quantity_available, item.lowstock_threshold)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {item.storage_location || 'Main Store'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={-1}
            rowsPerPage={inventoryRowsPerPage}
            page={inventoryPage}
            onPageChange={(e, newPage) => setInventoryPage(newPage)}
            onRowsPerPageChange={(e) => {
              setInventoryRowsPerPage(parseInt(e.target.value, 10));
              setInventoryPage(0);
            }}
          />
        </StyledTableContainer>
      </CardContent>
    </StyledCard>
  );

  // Render metric cards
  const renderMetricCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '0ms' }}>
          <MetricCard color={theme.palette.primary.main}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Items</Typography>
                  <Typography variant="h4" fontWeight={700}>{inventoryStats.total_items}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                  <Inventory />
                </Avatar>
              </Box>
            </CardContent>
          </MetricCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={6} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '100ms' }}>
          <MetricCard color={theme.palette.success.main}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
             
            </CardContent>
          </MetricCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={6} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '200ms' }}>
          <MetricCard color={theme.palette.warning.main}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">Low Stock Items</Typography>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {inventoryStats.low_stock_items}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </MetricCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={6} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '300ms' }}>
          <MetricCard color={theme.palette.error.main}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">Expiring Soon</Typography>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {inventoryStats.expiring_soon}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                  <Schedule />
                </Avatar>
              </Box>
            </CardContent>
          </MetricCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={6} sm={6} md={2.4}>
        <Zoom in={true} style={{ transitionDelay: '400ms' }}>
          <MetricCard color={theme.palette.info.main}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary">Out of Stock</Typography>
                  <Typography variant="h4" fontWeight={700} color="info.main">
                    {inventoryStats.out_of_stock}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                  <Block />
                </Avatar>
              </Box>
            </CardContent>
          </MetricCard>
        </Zoom>
      </Grid>
    </Grid>
  );

  // Render expiring products section
  const renderExpiringProducts = () => (
    <StyledCard>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: theme.palette.warning.main }} />
            Products Expiring Within 2 Weeks
          </Typography>
          <Badge badgeContent={expiringProducts.length} color="warning" />
        </Box>
        
        {expiringProducts.length === 0 ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            No products expiring within the next 2 weeks. Good inventory management!
          </Alert>
        ) : (
          <Stack spacing={1}>
            {expiringProducts.map((product, idx) => {
              const status = getExpiryStatus(product.days_until_expiry);
              return (
                <ExpiryAlertCard 
                  key={idx} 
                  severity={status.severity}
                  onClick={() => setSelectedProduct(product)}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={600}>
                          {product.item_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.category} • {product.storage_location || 'Main Store'}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Chip 
                          size="small" 
                          label={status.label}
                          color={status.color}
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                          Expires: {product.expiry_date}
                        </Typography>
                      </Box>
                    </Box>
                    <Box mt={1}>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.max(0, (product.days_until_expiry / 14) * 100)}
                        color={status.color === 'error' ? 'error' : status.color === 'warning' ? 'warning' : 'info'}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {product.days_until_expiry} days remaining • {product.quantity_received} {product.unit_of_measurement} remaining
                      </Typography>
                    </Box>
                  </CardContent>
                </ExpiryAlertCard>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </StyledCard>
  );

  // Render low stock products
  const renderLowStockProducts = () => (
    <StyledCard>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            <Warning sx={{ mr: 1, verticalAlign: 'middle', color: theme.palette.warning.main }} />
            Low Stock Alerts
          </Typography>
          <Badge badgeContent={lowStockProducts.length} color="warning" />
        </Box>
        
        {lowStockProducts.length === 0 ? (
          <Alert severity="success">
            All items have sufficient stock levels.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">Threshold</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lowStockProducts.map((product, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{product.item_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{product.category}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main" fontWeight={600}>
                        {product.current_stock} {product.unit_of_measurement}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {product.threshold} {product.unit_of_measurement}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={product.status}
                        color={product.status === 'Critical' ? 'error' : 'warning'}
                        sx={{ height: 24 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </StyledCard>
  );

  // Render charts section
  const renderCharts = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Stock by Category
            </Typography>
            <Box sx={{ height: 300 }}>
              {inventoryStats.category_distribution?.length > 0 ? (
                <Pie data={categoryChartData} options={chartOptions} />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No category data available</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
              Monthly Stock Movement
            </Typography>
            <Box sx={{ height: 300 }}>
              {inventoryStats.monthly_movement?.length > 0 ? (
                <Bar data={movementChartData} options={chartOptions} />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No movement data available</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>
      
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Department Consumption Analysis
            </Typography>
            <Box sx={{ height: 300 }}>
              {departmentConsumption.length > 0 ? (
                <Bar data={departmentChartData} options={chartOptions} />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No consumption data available</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>
    </Grid>
  );

  // Render recent requisitions
  const renderRecentRequisitions = () => (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
          Recent Requisitions
        </Typography>
        
        {recentRequisitions.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No recent requisitions
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentRequisitions.slice(0, 5).map((req) => (
                  <TableRow key={req.id} hover>
                    <TableCell>#{req.id}</TableCell>
                    <TableCell>{req.department}</TableCell>
                    <TableCell>{req.item_name}</TableCell>
                    <TableCell align="right">
                      {req.quantity_requested} {req.unit_of_measurement}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={req.status}
                        color={
                          req.status === 'Received' ? 'success' :
                          req.status === 'Issued' ? 'primary' :
                          req.status === 'Approved by GM' ? 'info' :
                          'warning'
                        }
                        sx={{ height: 24 }}
                      />
                    </TableCell>
                    <TableCell>{req.request_date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </StyledCard>
  );

  // Alert Dialog for expiring products
  const renderAlertDialog = () => (
    <Dialog 
      open={alertDialogOpen} 
      onClose={() => setAlertDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Inventory Alerts
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {(expiringProducts.length > 0 || expiredProducts.length > 0) && (
          <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
            <Typography variant="body2">
              <strong>Attention Required:</strong> There are products that require your immediate attention.
            </Typography>
          </Alert>
        )}
        
        {expiredProducts.length > 0 && (
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight={600} color="error.main" gutterBottom>
              Expired Products ({expiredProducts.length})
            </Typography>
            {expiredProducts.map((product, idx) => (
              <ExpiryAlertCard key={idx} severity="expired">
                <CardContent sx={{ p: 1.5 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{product.item_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expired: {product.expiry_date} ({Math.abs(product.days_overdue)} days overdue)
                      </Typography>
                    </Box>
                    <Chip size="small" label="EXPIRED" color="error" />
                  </Box>
                </CardContent>
              </ExpiryAlertCard>
            ))}
          </Box>
        )}
        
        {expiringProducts.length > 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="warning.main" gutterBottom>
              Products Expiring Soon ({expiringProducts.length})
            </Typography>
            {expiringProducts.slice(0, 5).map((product, idx) => {
              const status = getExpiryStatus(product.days_until_expiry);
              return (
                <ExpiryAlertCard key={idx} severity={status.severity}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{product.item_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expires: {product.expiry_date} ({product.days_until_expiry} days remaining)
                        </Typography>
                      </Box>
                      <Chip size="small" label={status.label} color={status.color} />
                    </Box>
                    <Box mt={1}>
                      <LinearProgress 
                        variant="determinate" 
                        value={(product.days_until_expiry / 14) * 100}
                        color={status.color === 'error' ? 'error' : 'warning'}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  </CardContent>
                </ExpiryAlertCard>
              );
            })}
            {expiringProducts.length > 5 && (
              <Typography variant="caption" color="text.secondary" textAlign="center" display="block" mt={1}>
                And {expiringProducts.length - 5} more products...
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAlertDialogOpen(false)} variant="contained">
          Acknowledge
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!isAuthorizedGM()) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 2 }}>
        <Alert severity="error" variant="filled">
          Access Denied. General Manager privileges required.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: isMobile ? 1.5 : 3,
        ml: isMobile ? 0 : (sidebarOpen ? 30 : 5),
        transition: 'margin 0.3s ease-in-out',
        width: '100%',
        maxWidth: '1600px',
        mx: 'auto',
        overflowY: 'auto',
        maxHeight: '100vh'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                <Assessment sx={{ mr: 2, verticalAlign: 'middle', fontSize: isMobile ? 28 : 32, color: theme.palette.primary.main }} />
                GM Inventory Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Real-time inventory insights and analytics for strategic decision making
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={20} /> : <FileDownload />}
                onClick={handleExportToExcel}
                disabled={exporting}
                size={isMobile ? "small" : "medium"}
              >
                Export Report
              </Button>
              <IconButton onClick={fetchDashboardData} size={isMobile ? "small" : "medium"}>
                <Refresh />
              </IconButton>
            </Stack>
          </Box>

          {/* Metric Cards */}
          {renderMetricCards()}

          {/* Main Content Tabs */}
          <Paper elevation={0} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 3,
                pt: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 48,
                }
              }}
            >
              <Tab icon={<Inventory />} label="Inventory Stock" iconPosition="start" />
              <Tab icon={<Analytics />} label="Overview" iconPosition="start" />
              <Tab icon={<ShowChart />} label="Charts & Analytics" iconPosition="start" />
              <Tab icon={<Warning />} label="Alerts & Risks" iconPosition="start" />
              <Tab icon={<Receipt />} label="Recent Activity" iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {activeTab === 0 && renderInventoryStockTable()}
              
              {activeTab === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {renderExpiringProducts()}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {renderLowStockProducts()}
                  </Grid>
                  <Grid item xs={12}>
                    {renderRecentRequisitions()}
                  </Grid>
                </Grid>
              )}
              
              {activeTab === 2 && renderCharts()}
              
              {activeTab === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {renderExpiringProducts()}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {renderLowStockProducts()}
                  </Grid>
                  {expiredProducts.length > 0 && (
                    <Grid item xs={12}>
                      <StyledCard>
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} color="error.main" gutterBottom>
                            <Block sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Expired Products
                          </Typography>
                          {expiredProducts.map((product, idx) => (
                            <ExpiryAlertCard key={idx} severity="expired">
                              <CardContent sx={{ p: 1.5 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>{product.item_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Expired on: {product.expiry_date}
                                    </Typography>
                                  </Box>
                                  <Chip size="small" label="EXPIRED" color="error" />
                                </Box>
                              </CardContent>
                            </ExpiryAlertCard>
                          ))}
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  )}
                </Grid>
              )}
              
              {activeTab === 4 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {renderRecentRequisitions()}
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Alert Dialog */}
      {renderAlertDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Grow}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GMInventoryDashboard;